import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { COPILOT_ACTIONS, getAction, type CopilotAction, type CopilotCtx } from './copilotActions';
import { respond, suggestionsForRoute } from './copilotEngine';
import { spotlight } from './spotlight';

export interface CopilotMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  actionIds?: string[];
  suggestions?: string[];
}

interface CopilotApi {
  panelOpen: boolean;
  paletteOpen: boolean;
  openPanel: () => void;
  togglePanel: () => void;
  closePanel: () => void;
  openPalette: () => void;
  closePalette: () => void;
  messages: CopilotMessage[];
  thinking: boolean;
  sendMessage: (text: string) => void;
  runAction: (id: string) => void;
  actions: CopilotAction[];
  routeSuggestions: string[];
}

const CopilotContext = createContext<CopilotApi | null>(null);

export function useCopilot(): CopilotApi {
  const ctx = useContext(CopilotContext);
  if (!ctx) throw new Error('useCopilot must be used within <CopilotProvider>');
  return ctx;
}

const GREETING: CopilotMessage = {
  id: 'greeting',
  role: 'assistant',
  text:
    "Hi! I'm your OmnibizChat Copilot 👋  I can explain what this platform does — or just set it up for you. What would you like to do?",
};

export function CopilotProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [panelOpen, setPanelOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [messages, setMessages] = useState<CopilotMessage[]>([GREETING]);
  const [thinking, setThinking] = useState(false);
  const idRef = useRef(1);
  const nextId = () => `m${idRef.current++}`;

  const closePanel = useCallback(() => setPanelOpen(false), []);
  const openPanel = useCallback(() => { setPanelOpen(true); setPaletteOpen(false); }, []);
  const togglePanel = useCallback(() => { setPanelOpen((o) => !o); setPaletteOpen(false); }, []);
  const openPalette = useCallback(() => { setPaletteOpen(true); setPanelOpen(false); }, []);
  const closePalette = useCallback(() => setPaletteOpen(false), []);

  const pushAssistant = useCallback(
    (text: string, extra?: { actionIds?: string[]; suggestions?: string[] }) =>
      setMessages((m) => [...m, { id: nextId(), role: 'assistant', text, ...extra }]),
    []
  );

  // The bridge the action registry uses to drive the app.
  const ctx: CopilotCtx = useMemo(
    () => ({
      navigate: (href) => navigate(href),
      spotlight: (target) => spotlight(target),
      toast: (message) => toast(message),
      say: (text, extra) => pushAssistant(text, extra),
      close: () => { setPanelOpen(false); setPaletteOpen(false); },
    }),
    [navigate, pushAssistant]
  );

  const runAction = useCallback(
    (id: string) => {
      const action = getAction(id);
      if (action) action.run(ctx);
    },
    [ctx]
  );

  const sendMessage = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      setMessages((m) => [...m, { id: nextId(), role: 'user', text: trimmed }]);
      setThinking(true);
      window.setTimeout(() => {
        const r = respond(trimmed);
        setThinking(false);
        pushAssistant(r.text, { actionIds: r.actionIds, suggestions: r.suggestions });
      }, 480);
    },
    [pushAssistant]
  );

  // ⌘K / Ctrl+K toggles the command palette; Esc closes both surfaces.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
        setPanelOpen(false);
      } else if (e.key === 'Escape') {
        setPaletteOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const value: CopilotApi = {
    panelOpen,
    paletteOpen,
    openPanel,
    togglePanel,
    closePanel,
    openPalette,
    closePalette,
    messages,
    thinking,
    sendMessage,
    runAction,
    actions: COPILOT_ACTIONS,
    routeSuggestions: suggestionsForRoute(location.pathname),
  };

  return <CopilotContext.Provider value={value}>{children}</CopilotContext.Provider>;
}
